/**
 * Sharing — "let this colleague see my project".
 *
 * The owner's own surface: you share what you own, with no administrator in the
 * loop. A separate admin surface (`/api/v1/grants`) can grant against anything,
 * for reassigning access when somebody leaves; this is not that.
 *
 * Enforcement needs no client help — a list simply returns less. What lives
 * here is the *granting* half, which has no other affordance.
 */

import type { components } from "./_generated/schema";
import { apiFetch } from "./apiFetch";

export type ShareOut = components["schemas"]["ShareOut"];
export type ShareListOut = components["schemas"]["ShareListOut"];
export type RecipientOut = components["schemas"]["RecipientOut"];
export type RecipientListOut = components["schemas"]["RecipientListOut"];

/** What a share lets someone do. Ordered widest-last, as the picker shows them. */
export const PERMISSIONS = ["read", "use", "edit"] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const PERMISSION_LABEL: Record<Permission, string> = {
  read: "Can view",
  use: "Can run",
  edit: "Can edit",
};

/** Said in the second person, because the dialog is read by the person granting. */
export const PERMISSION_HELP: Record<Permission, string> = {
  read: "Open it and follow along. No changes, no runs.",
  use: "Everything in Can view, plus running it.",
  edit: "Everything in Can run, plus changing it — saving versions, renaming.",
};

export function listShares(
  token: string,
  resourceType: string,
  resourceId: string,
): Promise<ShareListOut> {
  const params = new URLSearchParams({
    resource_type: resourceType,
    resource_id: resourceId,
  });
  return apiFetch<ShareListOut>(`/api/v1/shares?${params.toString()}`, { token });
}

/** Who this person could share with: the tenant's other users, plus the roles.
 *
 *  Not `GET /users` — that is the administration view and is Developer+, while
 *  sharing is open to every role. */
export function listRecipients(token: string): Promise<RecipientListOut> {
  return apiFetch<RecipientListOut>("/api/v1/shares/recipients", { token });
}

export function createShare(
  token: string,
  input: {
    resource_type: string;
    resource_id: string;
    subject_type: string;
    subject_id: string;
    permission: Permission;
    reason: string;
  },
): Promise<ShareOut> {
  return apiFetch<ShareOut>("/api/v1/shares", { method: "POST", token, json: input });
}

/** Stop sharing. The row is soft-revoked, so who could see what, and when,
 *  survives — a share that vanished without trace would be a hole in the
 *  access history. */
export function revokeShare(token: string, grantId: string, reason: string): Promise<void> {
  return apiFetch<void>(`/api/v1/shares/${encodeURIComponent(grantId)}`, {
    method: "DELETE",
    token,
    json: { reason },
  });
}
